import base from 'base-x';
import { Buffer } from 'node:buffer';
import { newUuid } from './uuid';
import { z } from 'zod';

const FUID_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const encoding = base(FUID_ALPHABET);

export const FUID_REGEX = /^[0-9A-Za-z]+$/;

export const FuidSchema = z.string().min(1).regex(FUID_REGEX, {
  message: 'ID must be a string of alphanumeric characters'
});

const fuidSymbol = Symbol('Fuid');
export type Fuid = string & { readonly [fuidSymbol]: unique symbol };

export function validateFuid(value: unknown): Fuid | undefined {
  try {
    return FuidSchema.parse(value) as Fuid;
  } catch {
    return undefined;
  }
}
/** Generates a new random FUID. */
export function newFuid(): Fuid {
  return uuidToFuid(newUuid());
}

/**
 * Converts a UUID string to a FUID string.
 * @param uuid The UUID string to convert.
 * @returns The FUID string.
 */
export function uuidToFuid(uuid: string): Fuid {
  const uuidBuffer = Buffer.from(uuid.replace(/-/g, ''), 'hex');
  return encoding.encode(new Uint8Array(uuidBuffer)) as Fuid;
}

/**
 * Converts a FUID string to a UUID string.
 * @param fuid The FUID string to convert.
 * @returns The UUID string.
 */
export function fuidToUuid(fuid: Fuid): string {
  const decodedBuffer = encoding.decode(fuid);
  const hexString = decodedBuffer.reduce(
    (acc, byte) => acc + byte.toString(16).padStart(2, '0'),
    ''
  );
  return `${hexString.slice(0, 8)}-${hexString.slice(8, 12)}-${hexString.slice(12, 16)}-${hexString.slice(16, 20)}-${hexString.slice(20)}`;
}

/**
 * Converts an integer to a FUID string.
 * @param num The integer to convert.
 * @returns The FUID string.
 */
export function intToFuid(num: number): Fuid {
  const numBigInt = BigInt(num);
  const numBuffer = Buffer.alloc(8);
  numBuffer.writeBigInt64BE(numBigInt, 0);

  const encodedBuffer = encoding.encode(new Uint8Array(numBuffer));
  return encodedBuffer.toString().replace(/^0+/, '') as Fuid;
}

/**
 * Converts a FUID string to an integer.
 * @param fuid The FUID string to convert.
 * @returns The integer.
 */
export function fuidToInt(fuid: Fuid): number {
  const decodedBuffer = encoding.decode(fuid);
  const fixedBuffer = Buffer.alloc(8);
  fixedBuffer.set(decodedBuffer, 8 - decodedBuffer.length);

  const numString = fixedBuffer.readBigInt64BE(0).toString();
  return parseInt(numString, 10);
}
